import { classNames, selectors, skipBtnTime } from './lib';
import { getEpisodeMetadata } from './metadata';
import { toDuration, toMinutes } from './utils';

window.itdAudioPlayers = {};

export const initAudioPlayer = (player) => {
	const episodeID = player.getAttribute('data-episode-id');
	const progressBar = player.querySelector(selectors.progressBar);
	let raf = null;

	// Control functions.
	const play = () => {
		if (!state.isPlaying) {
			state.isPlaying = true;
			state.audioEl.play();
			requestAnimationFrame(whilePlaying);
			player.setAttribute('data-play-state', 'play');
			updateMediaSessionAPI();
		}
	};

	const pause = () => {
		if (state.isPlaying) {
			state.isPlaying = false;
			state.audioEl.pause();
			cancelAnimationFrame(raf);
			player.setAttribute('data-play-state', 'pause');
		}
	};

	const stop = () => {
		state.audioEl.currentTime = 0;
		requestAnimationFrame(whilePlaying);
		pause();
	};

	const playFromBeginning = () => {
		state.audioEl.currentTime = 0;
		play();
	};

	// State.
	const state = {
		el: player,
		audioEl: player.querySelector(selectors.firstAudio),
		isPlaying: false,

		// Methods.
		play,
		pause,
		stop,
		playFromBeginning,
	};

	// Bind state to window object.
	window.itdAudioPlayers[`episode-${episodeID}`] = state;

	// Helper functions.
	const displayDuration = () => {
		player.querySelector(selectors.timeTotal).textContent = toDuration(state.audioEl.duration);
	};

	const updateRangeSlider = () => {
		progressBar.max = Math.floor(state.audioEl.duration);
	};

	/**
	 * Attaching Event Handlers: Control Buttons
	 */
	player.querySelector(selectors.btnPlayPause).addEventListener('click', () => {
		if (state.isPlaying) {
			pause();
		} else {
			play();
		}
	});
	player.querySelector(selectors.btnSkipBackward).addEventListener('click', () => {
		if (state.audioEl.currentTime > skipBtnTime) {
			state.audioEl.currentTime -= skipBtnTime;
			requestAnimationFrame(whilePlaying);
		} else {
			state.audioEl.currentTime = 0;
		}
	});
	player.querySelector(selectors.btnSkipForward).addEventListener('click', () => {
		if (state.audioEl.duration - state.audioEl.currentTime > skipBtnTime) {
			state.audioEl.currentTime += skipBtnTime;
			requestAnimationFrame(whilePlaying);
		}
	});

	// Audio.
	const whilePlaying = () => {
		progressBar.value = Math.floor(state.audioEl.currentTime);
		player.querySelector(selectors.timeCurrent).textContent = toDuration(progressBar.value);
		player.style.setProperty('--seek-before-width', `${(progressBar.value / progressBar.max) * 100}%`);
		raf = requestAnimationFrame(whilePlaying);
	};

	/**
	 * Buffering
	 */

	const displayBufferedAmount = (el) => {
		if (el === state.audioEl && state.audioEl.buffered.length > 0) {
			const bufferedAmount = Math.floor(state.audioEl.buffered.end(state.audioEl.buffered.length - 1));
			player.style.setProperty('--buffered-width', `${(bufferedAmount / progressBar.max) * 100}%`);
		}
	};

	/**
	 * Progress Bar
	 */

	// Handle Range Slider Input.
	progressBar.addEventListener('input', () => {
		// Show range progress.
		player.style.setProperty('--seek-before-width', (progressBar.value / progressBar.max) * 100 + '%');

		// Update current time.
		player.querySelector(selectors.timeCurrent).textContent = toDuration(progressBar.value);

		if (!state.audioEl.paused) {
			cancelAnimationFrame(raf);
		}
	});

	// Update audio currentTime on change.
	progressBar.addEventListener('change', () => {
		state.audioEl.currentTime = progressBar.value;

		if (!state.audioEl.paused) {
			requestAnimationFrame(whilePlaying);
		}
	});

	/**
	 * Playlist
	 */

	if (player.querySelector(selectors.playlist)) {
		player.querySelectorAll(selectors.playlistEpisode).forEach((episode) => {
			const associatedAudioEl = player.querySelector(`#${episode.getAttribute('data-target')}`);

			episode.addEventListener('click', () => {
				if (!episode.classList.contains(classNames.episodeActive)) {
					// De-select playing episode.
					player.querySelector(selectors.playlistActiveEpisode).classList.remove(classNames.episodeActive);

					// Select clicked episode.
					episode.classList.add(classNames.episodeActive);

					// Stop previous audio file.
					stop();

					// Load new audio element into state and reset play time.
					state.audioEl = associatedAudioEl;

					// Start new audio file.
					init();
					play();
				}
			});

			// Print episode duration.
			const printEpisodeDuration = () => {
				episode.querySelector(selectors.playlistEpisodeDuration).textContent = `${toMinutes(associatedAudioEl.duration)} min`;
			};

			if (associatedAudioEl.readyState > 0) {
				printEpisodeDuration();
			} else {
				associatedAudioEl.addEventListener('loadedmetadata', printEpisodeDuration);
			}
		});
	}

	/**
	 * Init.
	 */
	const init = () => {
		displayDuration();
		updateRangeSlider();
		displayBufferedAmount(state.audioEl);
	};

	if (state.audioEl.readyState > 0) {
		init();
	} else {
		state.audioEl.addEventListener('loadedmetadata', init);
	}

	player.querySelectorAll('audio').forEach((audioEl) => {
		// Reset play state when episode has ended.
		audioEl.addEventListener('ended', () => {
			stop();
		});

		// Update buffer size.
		audioEl.addEventListener('progress', () => {
			displayBufferedAmount(audioEl);
		});

		// Disable skip forward button if episode is about to end.
		audioEl.addEventListener('timeupdate', () => {
			const skipForwardBtn = player.querySelector(selectors.btnSkipForward);
			if (state.audioEl.duration - state.audioEl.currentTime <= skipBtnTime) {
				skipForwardBtn.disabled = true;
			} else if (skipForwardBtn.disabled) {
				skipForwardBtn.disabled = false;
			}
		});
	});

	/**
	 * Media Session API
	 */

	const updateMediaSessionAPI = async () => {
		if ('mediaSession' in navigator) {
			if (state.mediaMetadata) {
				navigator.mediaSession.metadata = state.mediaMetadata;
			} else {
				const episodeMetadata = await getEpisodeMetadata(episodeID);
				if (episodeMetadata.data) {
					state.mediaMetadata = new MediaMetadata(episodeMetadata.data);
					navigator.mediaSession.metadata = state.mediaMetadata;
				}
			}

			navigator.mediaSession.setActionHandler('play', play);
			navigator.mediaSession.setActionHandler('pause', pause);

			navigator.mediaSession.setActionHandler('seekbackward', (details) => {
				state.audioEl.currentTime -= details.seekOffset || skipBtnTime;
			});

			navigator.mediaSession.setActionHandler('seekforward', (details) => {
				state.audioEl.currentTime += details.seekOffset || skipBtnTime;
			});

			navigator.mediaSession.setActionHandler('seekto', (details) => {
				if (details.fastSeek && 'fastSeek' in state.audioEl) {
					state.audioEl.fastSeek(details.seekTime);
					return;
				}
				state.audioEl.currentTime = details.seekTime;
			});

			navigator.mediaSession.setActionHandler('stop', stop);
		}
	};
};
